import {ref} from 'vue';
import type {AuthCompleteResponseDto} from '@/features/auth/api/auth';

export type IdentLongPollPhase = 'idle' | 'waiting' | 'aborted' | 'failed' | 'succeeded';

/**
 *  Long-poll result for /api/app/auth/{method}/complete (Mobile-ID + Smart-ID).
 *  - `data` populated on 2xx with the camelCase `AuthCompleteResponseDto` body.
 *  - `errorCode` populated when the server returned a 4xx with `{errorCode}` body.
 *  - `aborted` flips when the caller invoked abort() before the request resolved.
 *  - `networkError` flips when fetch threw or the body wasn't parseable.
 */
export interface IdentLongPollResult {
    data?: AuthCompleteResponseDto;
    errorCode?: string;
    aborted?: boolean;
    networkError?: boolean;
}

export interface IdentLongPollHandle {
    /** Reactive phase the UI binds to so it can show the verification code, spinner, or error. */
    phase: ReturnType<typeof ref<IdentLongPollPhase>>;
    /** Optional last error code surfaced from the response body or a synthesized 'NetworkError'/'Aborted'. */
    errorCode: ReturnType<typeof ref<string | null>>;
    /** Issue the long-poll. Resolves with data, an aborted flag, or a network-error flag. */
    run: (url: string, init?: RequestInit) => Promise<IdentLongPollResult>;
    /** Cancel the in-flight long-poll; the run() promise resolves with aborted=true. */
    abort: () => void;
}

/**
 *  Single long-running fetch with AbortController plumbing. Reused by Mobile-ID and Smart-ID —
 *  neither needs a polling cadence, both block on a single Palk request that in turn blocks on
 *  Merit's Complete endpoint.
 *
 *  Parses the new /api/app/auth/* shape: HTTP 2xx with camelCase DTO body, HTTP 4xx with
 *  `{errorCode}` body. No legacy `{Code, ErrorCode}` envelope anymore.
 */
export function useIdentLongPoll(): IdentLongPollHandle {
    const phase = ref<IdentLongPollPhase>('idle');
    const errorCode = ref<string | null>(null);
    let controller: AbortController | null = null;

    function abort() {
        if (controller) {
            controller.abort();
            controller = null;
        }
    }

    async function run(url: string, init: RequestInit = {}): Promise<IdentLongPollResult> {
        abort();
        // Capture the controller in a local — abort() and subsequent run() calls reassign the
        // shared `controller` ref, so the catch block must consult its own AbortController to
        // tell "this call was cancelled" from "the next call started before this one finished".
        const myController = new AbortController();
        controller = myController;
        phase.value = 'waiting';
        errorCode.value = null;

        let response: Response;
        try {
            response = await fetch(url, {...init, signal: myController.signal});
        } catch (err) {
            if (myController.signal.aborted) {
                phase.value = 'aborted';
                errorCode.value = 'Aborted';
                return {aborted: true};
            }
            phase.value = 'failed';
            errorCode.value = 'NetworkError';
            return {networkError: true};
        } finally {
            if (controller === myController) {
                controller = null;
            }
        }

        if (response.ok) {
            try {
                const data = (await response.json()) as AuthCompleteResponseDto;
                phase.value = 'succeeded';
                return {data};
            } catch {
                phase.value = 'failed';
                errorCode.value = 'NetworkError';
                return {networkError: true};
            }
        }

        try {
            const body = (await response.json()) as { errorCode?: string };
            phase.value = 'failed';
            errorCode.value = body?.errorCode ?? null;
            return {errorCode: body?.errorCode};
        } catch {
            phase.value = 'failed';
            errorCode.value = 'NetworkError';
            return {networkError: true};
        }
    }

    return {phase, errorCode, run, abort};
}
